import type { TFunction } from 'i18next';
import type { JSX, ReactNode } from 'react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import type { DecisionImage } from '../../../../lib/images.js';
import styles from './Images.module.css';

/** Une œuvre à laquelle le fichier peut se rattacher. */
export interface OptionOeuvre {
  oeuvreId: string;
  inventaire: string;
  titre: string;
  /** Le rang que le nom du fichier donne ; `null` : il n'en dit rien. */
  ordre: number | null;
}

/** Au plus ce nombre d'œuvres proposées à la fois quand on cherche parmi toutes. */
const PROPOSEES_MAX = 8;

const cleDe = (option: OptionOeuvre): string => `${option.oeuvreId}:${String(option.ordre)}`;

/** Le texte d'une décision, pour la ligne qui la rappelle. */
export function resumeDecision(
  t: TFunction,
  decision: DecisionImage,
  etiquette: (oeuvreId: string) => string,
): string {
  if (decision.action === 'ignorer') return String(t('images.decision.ignore'));
  return String(
    decision.ordre === undefined
      ? t('images.decision.rattache', { oeuvre: etiquette(decision.oeuvreId) })
      : t('images.decision.rattacheOrdre', {
          oeuvre: etiquette(decision.oeuvreId),
          ordre: decision.ordre,
        }),
  );
}

/**
 * Un fichier qui attend une décision de la galerie : le rattacher à une
 * œuvre (avec son rang) ou l'ignorer. Les candidates sont celles que l'API
 * rend ; pour un orphelin, on cherche parmi les œuvres sans image. Rien n'est
 * envoyé d'ici : la décision rejoint le brouillon de la revue.
 */
export function FichierADecider({
  chemin,
  motif,
  details,
  options,
  recherche,
  decision,
  etiquette,
  desactive,
  onDecider,
}: {
  chemin: string;
  motif: string;
  details?: ReactNode;
  /** Les œuvres proposées : les candidates d'un ambigu. */
  options: OptionOeuvre[];
  /** Orphelin : toutes les œuvres entre lesquelles chercher ; `options` est alors ignoré. */
  recherche?: OptionOeuvre[];
  decision: DecisionImage | undefined;
  etiquette: (oeuvreId: string) => string;
  desactive: boolean;
  onDecider: (decision: DecisionImage | null) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const identifiant = useId();
  const [requete, setRequete] = useState('');
  const [choisie, setChoisie] = useState<string | null>(() => {
    if (decision?.action !== 'rattacher') return null;
    const trouvee = options.find((option) => option.oeuvreId === decision.oeuvreId);
    return trouvee ? cleDe(trouvee) : null;
  });
  const [ordre, setOrdre] = useState(
    decision?.action === 'rattacher' && decision.ordre !== undefined ? String(decision.ordre) : '',
  );

  const proposees = useMemo(() => {
    if (!recherche) return options;
    const mot = requete.trim().toLowerCase();
    if (mot === '') return recherche.slice(0, PROPOSEES_MAX);
    return recherche
      .filter((option) => `${option.inventaire} ${option.titre}`.toLowerCase().includes(mot))
      .slice(0, PROPOSEES_MAX);
  }, [options, recherche, requete]);
  // Une œuvre déjà choisie reste visible même si la recherche ne la montre plus.
  const affichees = useMemo(() => {
    const choix = [...options, ...(recherche ?? [])].find((option) => cleDe(option) === choisie);
    return choix && !proposees.some((option) => cleDe(option) === choisie)
      ? [choix, ...proposees]
      : proposees;
  }, [choisie, options, proposees, recherche]);

  const option = affichees.find((candidate) => cleDe(candidate) === choisie);
  const ordreSaisi = ordre.trim() === '' ? undefined : Number(ordre);
  const ordreValide = ordreSaisi === undefined || (Number.isInteger(ordreSaisi) && ordreSaisi >= 1);

  const rattacher = (): void => {
    if (!option || !ordreValide) return;
    onDecider({
      action: 'rattacher',
      oeuvreId: option.oeuvreId,
      ...(ordreSaisi === undefined ? {} : { ordre: ordreSaisi }),
    });
  };

  return (
    <li className={styles.fichier} data-etat={decision ? 'tranche' : 'a_trancher'}>
      <div className={styles.fichierEntete}>
        <span className={styles.chemin}>{chemin}</span>
        <span className={styles.motif}>{motif}</span>
      </div>
      {details}

      {recherche ? (
        <div className={styles.recherche}>
          <label htmlFor={`${identifiant}-recherche`}>{t('images.decision.chercher')}</label>
          <Input
            id={`${identifiant}-recherche`}
            type="search"
            value={requete}
            disabled={desactive}
            placeholder={t('images.decision.chercherAide')}
            onChange={(evenement) => setRequete(evenement.target.value)}
          />
        </div>
      ) : null}

      {affichees.length === 0 ? (
        <p className={styles.aucuneOeuvre}>{t('images.decision.aucuneOeuvre')}</p>
      ) : (
        <fieldset className={styles.candidats}>
          <legend>
            {recherche ? t('images.decision.oeuvres') : t('images.decision.candidates')}
          </legend>
          {affichees.map((candidate) => {
            const cle = cleDe(candidate);
            return (
              <label key={cle} className={styles.candidat}>
                <input
                  type="radio"
                  name={`${identifiant}-oeuvre`}
                  value={cle}
                  checked={choisie === cle}
                  disabled={desactive}
                  onChange={() => {
                    setChoisie(cle);
                    if (candidate.ordre !== null) setOrdre(String(candidate.ordre));
                  }}
                />
                <span className={styles.candidatTexte}>
                  <span className={styles.candidatNumero}>{candidate.inventaire}</span>
                  <span className={styles.candidatTitre}>
                    {candidate.titre}
                    {candidate.ordre === null
                      ? ''
                      : ` · ${t('images.decision.ordreDuNom', { ordre: candidate.ordre })}`}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>
      )}

      <div className={styles.reglage}>
        <div className={styles.ordre}>
          <label htmlFor={`${identifiant}-ordre`}>{t('images.decision.ordre')}</label>
          <Input
            id={`${identifiant}-ordre`}
            type="number"
            inputMode="numeric"
            min={1}
            max={999}
            value={ordre}
            disabled={desactive}
            aria-invalid={ordreValide ? undefined : true}
            onChange={(evenement) => setOrdre(evenement.target.value)}
          />
        </div>
        <div className={styles.actionsFichier}>
          <Button disabled={desactive || !option || !ordreValide} onClick={rattacher}>
            {t('images.decision.rattacher')}
          </Button>
          <Button
            variant="secondary"
            disabled={desactive}
            onClick={() => onDecider({ action: 'ignorer' })}
          >
            {t('images.decision.ignorer')}
          </Button>
        </div>
      </div>
      <span className={styles.motif}>{t('images.decision.ordreAide')}</span>

      {decision ? (
        <div className={styles.fichierEntete}>
          <p className={styles.decision} role="status">
            {resumeDecision(t, decision, etiquette)}
          </p>
          <Button variant="ghost" size="sm" disabled={desactive} onClick={() => onDecider(null)}>
            {t('images.decision.retirer')}
          </Button>
        </div>
      ) : null}
    </li>
  );
}
